//-- Copyright (c) 2021 TMT Insights Group, LLC
import { colorConsoleLog } from '@/_utils/utils';
import ErrorFallback from '@/components/_common/ErrorFallback';
import BulkActionBar from '@/components/_common/Table/BulkActionBar';
import { getNextSortOrder, SortOrder } from '@/components/_common/Table/sortOrders';
import Table from '@/components/_common/Table/Table';
import TableCounts from '@/components/_common/Table/TableFooter/TableCounts';
import TableFooter from '@/components/_common/Table/TableFooter/TableFooter';
import TablePageSize from '@/components/_common/Table/TableFooter/TablePageSize';
import TablePagination from '@/components/_common/Table/TableFooter/TablePagination';
import TableNoResults from '@/components/_common/Table/TableNoResults';
import useColumns from '@/components/_hooks/useColumns';
import { LoadingStatuses } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectAuth } from '@/redux/selectors/authSelectors';
import {
  selectBulkErrors,
  selectCheckedTableRows,
  selectCurrentTable,
  selectCurrentTableKey,
  selectDataTableIsMultiSort,
  selectDataTablePage,
  selectDataTableParams,
  selectDataTableSettings,
  selectDataTableSorting,
  selectDataTableType,
  selectIsExpandableTable,
  selectIsFiltered
} from '@/redux/selectors/dataTableSelectors';
import { selectFocusCalendarView } from '@/redux/selectors/focusCalendarSelectors';
import { updatePageSize } from '@/redux/slices/authSlice';
import { clearFilters, collapseAllRows, getData, selectRowHeader, setCurrentPage, setPageSize, setSorting } from '@/redux/slices/dataTableSlice';
import styled from '@emotion/styled';
import { Container, ContainerProps, Grid } from '@mui/material';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import BulkActionErrorModal from '../BulkActionErrorModal';
import CalendarContainer from './Calendar/Calendar';

type TableContainerProps = ContainerProps & {
  onRowClick?: (e: React.MouseEvent, item?: any) => void;
  simplified?: boolean;
  activeRowId?: number;
  onReorderRows?: (taskId: number, priority: number) => Promise<void>;
  overrideData?: any[]; // TODO: TEMPORARY - ONLY FOR UNI POC
};

const getScrollEnd = (ref: RefObject<any>) => {
  if (Math.floor(ref.current.scrollLeft) === 0) return 'left';
  else if (Math.ceil(ref.current.scrollLeft) + ref.current.clientWidth >= ref.current.scrollWidth) return 'right';
  else return null;
};

const TableContainer = ({ onRowClick, simplified, activeRowId, onReorderRows, overrideData, ...tableProps }: TableContainerProps) => {
  const dispatch = useAppDispatch();
  const { data, status, pagination } = useAppSelector(selectCurrentTable);
  const tableType = useAppSelector(selectDataTableType);
  const { status: profileStatus } = useAppSelector(selectAuth);
  const isMultiSort = useAppSelector(selectDataTableIsMultiSort);
  const { order, id } = useAppSelector(selectDataTableSorting);
  const sorting = isMultiSort ? useAppSelector(selectDataTableSorting) : [];
  const currentPage = useAppSelector(selectDataTablePage);
  const tableSettings = useAppSelector(selectDataTableSettings);
  const tableParams = useAppSelector(selectDataTableParams);
  const { columns } = useColumns();
  const tableKey = useAppSelector(selectCurrentTableKey);
  const isFiltered = useAppSelector(selectIsFiltered);
  const isExpandableTable = useAppSelector(selectIsExpandableTable);
  const selectedRows = useAppSelector(selectCheckedTableRows);
  const bulkErrors = useAppSelector(selectBulkErrors);
  const calendarView = useAppSelector(selectFocusCalendarView);
  const loading = useMemo(() => status === LoadingStatuses.pending || profileStatus === LoadingStatuses.pending, [status, profileStatus]);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState<boolean | null>(null);
  const [scrollEnd, setScrollEnd] = useState<'left' | 'right' | null>('left');
  const isCalendarTable = tableType === 'calendar';
  const isCalendarDailyView = calendarView === 'day';

  useEffect(() => {
    const handleResize = () => {
      const newScrollable = scrollableRef.current && scrollableRef.current.scrollWidth > scrollableRef.current.clientWidth;
      if (scrollableRef.current && newScrollable !== scrollable) {
        setScrollable(newScrollable);
      }
    };

    if (scrollableRef.current && !loading) {
      const isScrollable = scrollableRef.current?.scrollWidth > scrollableRef.current?.clientWidth;
      setScrollable(isScrollable);
      window.addEventListener('resize', handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [scrollableRef.current, scrollable, loading]);

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
    dispatch(selectRowHeader([]));
    dispatch(collapseAllRows({ tableKey }));
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // always change the current page back to 1, since we're not sure if the total number of pages will exceed 1 page
    dispatch(setCurrentPage(1));
    dispatch(setPageSize(newPageSize));
    dispatch(selectRowHeader([]));
    dispatch(collapseAllRows({ tableKey }));
    dispatch(updatePageSize(newPageSize));
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!tableSettings.disableGetData) {
      dispatch(getData());
    }
  }, [tableSettings, tableParams.status_filter]);

  const handleClearFilters = () => dispatch(clearFilters(undefined));

  const onSortColumn = (newSortId: string) => {
    let sortOrder = SortOrder.Ascending;
    if (!isMultiSort) {
      if (newSortId !== id) {
        sortOrder = SortOrder.Ascending;
      } else {
        sortOrder = getNextSortOrder(order) as 'asc' | 'desc';
      }
    } else {
      if (sorting.length !== 1) {
        sortOrder = SortOrder.Ascending;
      } else {
        const firstChar = sorting[0].at(0);
        if (firstChar !== '-' && sorting[0] === newSortId) {
          sortOrder = SortOrder.Descending;
        } else {
          sortOrder = SortOrder.Ascending;
        }
      }
    }
    dispatch(setSorting({ id: String(newSortId), order: sortOrder as 'asc' | 'desc' }));
  };

  if (!columns?.length && !isCalendarTable) {
    colorConsoleLog(`ERROR: No configs/columns/${tableKey}.tsx found.`, 'red');
  }

  const handleScroll = () => {
    const newScrollEnd = getScrollEnd(scrollableRef);
    if (newScrollEnd !== scrollEnd) {
      setScrollEnd(newScrollEnd);
    }
  };

  return (
    <ErrorBoundary FallbackComponent={() => ErrorFallback({ error: 'There was an error.' })} resetKeys={[data]}>
      {/* used to render a fallback UI when an error occurs somewhere in the table */}
      <Wrapper
        expandable={+isExpandableTable}
        simplified={+(simplified || false)}
        style={simplified ? { padding: 0 } : {}}
        {...tableProps}
        last={columns[columns.length - 1]?.id}
      >
        <ScrollableDiv
          scrollable={+(scrollable || false)}
          ref={scrollableRef}
          onScroll={handleScroll}
          last={columns[columns.length - 1]?.id}
          first={columns[0]?.id}
          style={{ overflowX: isCalendarTable ? 'auto' : 'scroll' }}
        >
          {(!data?.length || !columns.length) && !loading && !isCalendarTable ? (
            <TableNoResults handleClearFilters={handleClearFilters} isClearable={isFiltered && !simplified} />
          ) : (
            <>
              {isCalendarTable ? (
                <CalendarContainer loading={loading} />
              ) : (
                <Table
                  columns={columns}
                  data={overrideData || data}
                  onRowClick={onRowClick}
                  loading={loading}
                  onReorderRows={onReorderRows}
                  onSortColumn={onSortColumn}
                  activeRowId={activeRowId}
                  scrollable={scrollable || false}
                  scrollEnd={scrollEnd}
                />
              )}
            </>
          )}
        </ScrollableDiv>
        {!overrideData && !loading && !!data?.length && (!isCalendarTable || isCalendarDailyView) && (
          <TableFooter container>
            <Grid item>
              <TableCounts
                currentPage={currentPage}
                pageSize={pagination!.page_size || data.length}
                totalCount={pagination!.total || data.length}
                tableKey={tableKey as string}
              />
            </Grid>
            {pagination!.total > pagination!.page_size && (
              <PaginationWrapper item>
                <TablePagination
                  currentPage={currentPage}
                  pageSize={pagination!.page_size}
                  totalCount={pagination!.total}
                  onPageChange={handlePageChange}
                />
              </PaginationWrapper>
            )}
            {pagination!.page_size && (
              <PageSizeWrapper item>
                <TablePageSize currentPageSize={pagination!.page_size} onPageSizeChange={handlePageSizeChange} />
              </PageSizeWrapper>
            )}
          </TableFooter>
        )}
        {selectedRows.length > 0 && <BulkActionBar />}
        {bulkErrors.length > 0 && <BulkActionErrorModal />}
      </Wrapper>
    </ErrorBoundary>
  );
};

export default TableContainer;

const ScrollableDiv = styled.div<{ scrollable: number; last: string; first: string }>`
  width: 100%;

  ${({ scrollable, first, theme }) =>
    (first === 'expandable' || first === 'priority') &&
    scrollable &&
    `
  table .table-row > td:first-of-type {
    background-color: ${theme.palette.background.paper};
    position: sticky;
    left: 0;
    z-index: 2;
  }

  table .table-head-sortable th:first-of-type {
    opacity: 1;
    background-color: ${theme.palette.background.default};
    position: sticky;
    left: 0;
    z-index: 2;

    & span > svg {
      opacity: .5;
    }
  }
`}

  ${({ scrollable, last, theme }) =>
    last === 'overflow' &&
    scrollable &&
    `
    table .table-row > td:last-of-type {
      background-color: ${theme.palette.background.paper};
      position: sticky;
      right: -1px;
      z-index: 2;
    }

    table  .table-head-sortable th:last-of-type {
      background-color: ${theme.palette.background.default};
      opacity: 1;
      position: sticky;
      right: 0;
      z-index: 2;
    }
  `}
`;

const Wrapper = styled(Container) <{ expandable: number; simplified: number; last: string }>`
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ simplified }) => (simplified ? 0 : 'inherit')};
  background: ${({ expandable }) => (expandable ? 'transparent' : 'inherit')};
  width: 100%;
  display: block;
`;

const PaginationWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;

const PageSizeWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
`;
